import React, { useEffect, useRef } from 'react';
import { x } from '@xstyled/styled-components';
import { t } from 'ttag';
import { useFocusTrap } from '~/lib/a11y/useFocusTrap';
import { LlamaModal } from '~/ui/llama-modal/LlamaModal';
import { withTransition } from '~/ui/transition/Transition';
import { Overlay } from '~/ui/overlay/Overlay';
import { ConfidentialHotline } from './comments/ConfidentialHotline';
import { Faq } from './faq/Faq';
import { OpenHelpMenuButton } from './OpenHelpMenuButton';
import { ReportIssue } from './comments/ReportIssue';
import { Root } from './Root';
import { HelpPages, useHelpStore } from './Store';
import { useOpenCloseAudioEffect } from '~/ui/audio-effect/useOpenCloseAudioEffect';

const SettingsUI = () => {
  const helpDialogRef = useRef<HTMLDivElement>(null);
  const helpTriggerRef = useRef<HTMLButtonElement>(null);
  const { open, toggle, page, setPage } = useHelpStore();
  const OverlayWithTransition = withTransition(Overlay);
  useOpenCloseAudioEffect(open);
  useFocusTrap(helpDialogRef, open, undefined, helpTriggerRef);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;

      // Capture + stop so LlamaModal's document Escape listener cannot
      // close the whole drawer while a sub-page should only go Back.
      event.stopImmediatePropagation();

      if (page !== HelpPages.Root) {
        setPage(HelpPages.Root);
        return;
      }

      toggle(false);
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [open, page, setPage, toggle]);

  // `#root.inert` is owned by Overlay's ref-counted lock — do not set it here.

  return (
    <>
      <OpenHelpMenuButton ref={helpTriggerRef} />
      <OverlayWithTransition
        show={open}
        open={open}
        onClick={() => toggle(false)}
      >
        <x.div
          ref={helpDialogRef}
          role="dialog"
          aria-modal="true"
          // CAT-1232 / WCAG 2.4.6: FAQ dialog name must match its topic, not "Help".
          aria-label={page === HelpPages.FAQ ? undefined : t`Help`}
          aria-labelledby={
            page === HelpPages.FAQ ? 'help-faq-title' : undefined
          }
          h="100%"
          w="100%"
        >
          {page !== HelpPages.FAQ ? (
            <LlamaModal
              horizontal="right"
              vertical="top"
              close={() => toggle(false)}
              allowScroll
            >
              <Root />
              <ReportIssue />
              <ConfidentialHotline />
            </LlamaModal>
          ) : (
            <Faq />
          )}
        </x.div>
      </OverlayWithTransition>
    </>
  );
};

export const Help = () => (
  <>
    <SettingsUI />
  </>
);
